export interface CalculationResult {
  t_so_luong: number;
  t_thue: number;
  t_tt: number;
  t_cp: number;
}

export function calculateTotals(
  detailItems: MuaHangRecord[] = [],
  feeItems: MuaHangRecord[] = [],
  taxItems: MuaHangRecord[] = []
): CalculationResult {
  const t_so_luong = detailItems.reduce((sum, item) => sum + (parseFloat(item.thanh_tien) || 0), 0);
  const t_thue = Math.round(taxItems.reduce((sum, item) => sum + (parseFloat(item.t_thue_nt) || 0), 0) * 10) / 10;
  const t_cp = Math.round(feeItems.reduce((sum, item) => sum + (parseFloat(item.tien_cp_nt) || 0), 0) * 10) / 10;
  const t_tt = Math.round((t_cp + t_thue) * 10) / 10;

  return {
    t_so_luong,
    t_thue,
    t_tt,
    t_cp
  };
}

/**
 * Calculate total tien_cp_nt from fee rows
 */
export function getTotalFee(feeRows: MuaHangRecord[]): number {
  return feeRows.reduce((sum, row) => sum + (Number(row.tien_cp_nt) || 0), 0);
}

function getFeeAllocationTotals(feeRows: MuaHangRecord[] = []): { quantity: number; amount: number } {
  return feeRows.reduce(
    (totals, row) => {
      const allocationType = (row?.ma_cp_data?.loai_pb ?? row?.loai_pb)?.toString();
      const feeValue = Number(row?.tien_cp_nt) || 0;

      if (allocationType === '1') {
        totals.quantity += feeValue;
      } else {
        totals.amount += feeValue;
      }

      return totals;
    },
    { quantity: 0, amount: 0 }
  );
}

/**
 * Calculate total from detail rows based on the provided field
 */
export function getTotal(detailRows: MuaHangRecord[] = [], field: 'tien0' | 'so_luong' = 'tien0'): number {
  return detailRows.reduce((sum, row) => sum + (Number(row?.[field]) || 0), 0);
}

/**
 * Calculate proportional value for a single detail row
 * Formula: result = (baseValue / totalBase) * targetTotal
 */
export function calculateForRow(baseValue: number, totalBase: number, targetTotal: number): number {
  if (totalBase === 0 || targetTotal === 0) {
    return 0;
  }

  const proportion = baseValue / totalBase;
  const result = proportion * targetTotal;
  return Math.round(result * 10) / 10;
}

const applyAllocatedValues = (
  detailRows: MuaHangRecord[],
  allocator: (detailRow: MuaHangRecord) => { cp_nt: number; thue_nt: number }
) => {
  let hasChanges = false;

  const nextRows = detailRows.map(detailRow => {
    const allocatedValues = allocator(detailRow);

    if (Object.is(detailRow.cp_nt, allocatedValues.cp_nt) && Object.is(detailRow.thue_nt, allocatedValues.thue_nt)) {
      return detailRow;
    }

    hasChanges = true;

    return {
      ...detailRow,
      ...allocatedValues
    };
  });

  return hasChanges ? nextRows : detailRows;
};

/**
 * Recalculate cp_nt and thue_nt for all detail rows based on current totals from fee and tax rows
 */
export function recalculateForAllRows(
  detailRows: MuaHangRecord[],
  feeRows: MuaHangRecord[],
  taxRows: MuaHangRecord[] = []
): MuaHangRecord[] {
  const { quantity: totalFeeByQuantity, amount: totalFeeByAmount } = getFeeAllocationTotals(feeRows);
  const totalQuantity = getTotal(detailRows, 'so_luong');
  const totalAmount = getTotal(detailRows, 'tien0');
  const totalTax = taxRows.reduce((sum, row) => sum + (Number(row.t_thue_nt) || 0), 0);

  return applyAllocatedValues(detailRows, detailRow => {
    const soLuong = Number(detailRow?.so_luong) || 0;
    const tien0 = Number(detailRow?.tien0) || 0;
    const quantityShare = calculateForRow(soLuong, totalQuantity, totalFeeByQuantity);
    const amountShare = calculateForRow(tien0, totalAmount, totalFeeByAmount);
    const cp_nt = quantityShare + amountShare;
    const thue_nt = calculateForRow(tien0, totalAmount, totalTax);

    return {
      cp_nt,
      thue_nt
    };
  });
}

/**
 * Calculate cp_nt and thue_nt for all detail rows when values change
 */
export function calculateOnChange(
  detailRows: MuaHangRecord[],
  feeRows: MuaHangRecord[],
  taxRows: MuaHangRecord[] = []
): MuaHangRecord[] {
  const { quantity: totalFeeByQuantity, amount: totalFeeByAmount } = getFeeAllocationTotals(feeRows);
  const totalQuantity = getTotal(detailRows, 'so_luong');
  const totalAmount = getTotal(detailRows, 'tien0');
  const totalTax = taxRows.reduce((sum, row) => sum + (Number(row.t_thue_nt) || 0), 0);
  const totalFee = totalFeeByQuantity + totalFeeByAmount;

  return applyAllocatedValues(detailRows, detailRow => {
    const soLuong = Number(detailRow?.so_luong) || 0;
    const tien0 = Number(detailRow?.tien0) || 0;
    const quantityShare = calculateForRow(soLuong, totalQuantity, totalFeeByQuantity);
    const amountShare = calculateForRow(tien0, totalAmount, totalFeeByAmount);

    const cp_nt = totalFee !== 0 ? quantityShare + amountShare : detailRow.cp_nt || 0;

    const thue_nt = totalTax !== 0 ? calculateForRow(tien0, totalAmount, totalTax) : detailRow.thue_nt || 0;

    return {
      cp_nt,
      thue_nt
    };
  });
}
